import React, { useState, useEffect, useMemo, useCallback } from "react";
import { apiService } from "../services/api";
import {
  DataRequestSource,
  RedirectableUser,
  RedirectTargetsResponse,
} from "../types/api";

interface ForwardRequestPanelProps {
  requestId: number;
  onCancel: () => void;
  // Called after a successful forward so the parent can refetch the list.
  onForwarded: () => void;
}

const ChevronIcon: React.FC<{ open: boolean }> = ({ open }) => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    style={{
      flexShrink: 0,
      transform: open ? "rotate(90deg)" : "none",
      transition: "transform 0.15s",
    }}
  >
    <path d="m9 18 6-6-6-6" />
  </svg>
);

/**
 * Forwards a data request to the people or organisations that hold the data.
 *
 * Ported from the pre-revamp DataRequestDetail: an org tree with tri-state
 * checkboxes, where checking an organisation selects all of its selectable
 * members. Targets this request was already forwarded to are shown checked and
 * disabled, so a request is never forwarded to the same recipient twice.
 *
 * Organisation IDs are *derived*, not picked directly: an org is sent as a
 * target only when every one of its still-selectable members is checked. That
 * mirrors the backend's model, where an org-level source row means "anyone at
 * this org may respond" while user rows name individuals.
 */
export const ForwardRequestPanel: React.FC<ForwardRequestPanelProps> = ({
  requestId,
  onCancel,
  onForwarded,
}) => {
  const [targets, setTargets] = useState<RedirectTargetsResponse | null>(null);
  const [sources, setSources] = useState<DataRequestSource[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedUserIDs, setSelectedUserIDs] = useState<Set<number>>(
    new Set(),
  );
  const [expandedOrgs, setExpandedOrgs] = useState<Set<number>>(new Set());
  const [notes, setNotes] = useState("");
  const [inviting, setInviting] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteName, setInviteName] = useState("");
  const [inviteOrgName, setInviteOrgName] = useState("");
  const [inviteSubmitting, setInviteSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        // Forwarding history lives on its own endpoint — the list payload the
        // split view renders from does not carry `sources`.
        const [t, s] = await Promise.all([
          apiService.getRedirectTargets(),
          apiService
            .getDataRequestSources(requestId)
            .catch(() => ({ sources: [], total: 0 })),
        ]);
        if (cancelled) return;
        setTargets(t);
        setSources(s.sources || []);
      } catch (err) {
        if (cancelled) return;
        setError(
          err instanceof Error ? err.message : "Failed to load forward targets",
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [requestId]);

  const allUsers = useMemo<RedirectableUser[]>(() => {
    const merged = [
      ...(targets?.data_uploaders || []),
      ...(targets?.pis || []),
    ];
    const seen = new Set<number>();
    return merged.filter((u) => {
      if (seen.has(u.id)) return false;
      seen.add(u.id);
      return true;
    });
  }, [targets]);

  // Recipients this request already went to — checked and locked.
  const prevUserIDs = useMemo(
    () =>
      new Set(sources.filter((s) => s.user_id).map((s) => s.user_id as number)),
    [sources],
  );
  const prevOrgIDs = useMemo(
    () =>
      new Set(
        sources
          .filter((s) => s.organization_id && !s.user_id)
          .map((s) => s.organization_id as number),
      ),
    [sources],
  );

  const { orgMembers, unaffiliated } = useMemo(() => {
    const byOrg = new Map<number, RedirectableUser[]>();
    const loose: RedirectableUser[] = [];
    for (const u of allUsers) {
      if (u.organization_id) {
        const list = byOrg.get(u.organization_id) || [];
        list.push(u);
        byOrg.set(u.organization_id, list);
      } else {
        loose.push(u);
      }
    }
    return { orgMembers: byOrg, unaffiliated: loose };
  }, [allUsers]);

  // An org is forwarded to as a whole only when all its selectable members are
  // checked; that is what the backend records as an org-level source.
  const derivedOrgIDs = useCallback((): number[] => {
    const result: number[] = [];
    for (const org of targets?.organizations || []) {
      if (prevOrgIDs.has(org.id)) continue;
      const members = orgMembers.get(org.id) || [];
      const selectable = members.filter((u) => !prevUserIDs.has(u.id));
      if (selectable.length === 0) continue;
      if (selectable.every((u) => selectedUserIDs.has(u.id)))
        result.push(org.id);
    }
    return result;
  }, [targets, orgMembers, prevOrgIDs, prevUserIDs, selectedUserIDs]);

  const toggleUser = (id: number) =>
    setSelectedUserIDs((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleOrg = (members: RedirectableUser[]) => {
    const selectable = members.filter((u) => !prevUserIDs.has(u.id));
    const allSelected =
      selectable.length > 0 &&
      selectable.every((u) => selectedUserIDs.has(u.id));
    setSelectedUserIDs((prev) => {
      const next = new Set(prev);
      selectable.forEach((u) =>
        allSelected ? next.delete(u.id) : next.add(u.id),
      );
      return next;
    });
  };

  const toggleExpand = (id: number) =>
    setExpandedOrgs((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const handleSubmit = async () => {
    if (selectedUserIDs.size === 0) return;
    setSubmitting(true);
    setError(null);
    try {
      const orgIDs = derivedOrgIDs();
      await apiService.redirectDataRequest(requestId, {
        to_user_ids: Array.from(selectedUserIDs),
        to_org_ids: orgIDs.length > 0 ? orgIDs : undefined,
        notes: notes.trim() || "Forwarded to data source",
      });
      onForwarded();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to forward request",
      );
      setSubmitting(false);
    }
  };

  const handleInvite = async () => {
    const email = inviteEmail.trim();
    if (!email) return;
    setInviteSubmitting(true);
    setError(null);
    try {
      // Match a typed organisation to an existing one so the invitee lands in
      // the right org rather than creating a duplicate.
      const typedOrg = inviteOrgName.trim();
      const matched = typedOrg
        ? (targets?.organizations || []).find(
            (o) => o.name.toLowerCase() === typedOrg.toLowerCase(),
          )
        : undefined;
      await apiService.inviteUploader(
        email,
        notes.trim() || undefined,
        matched?.id,
        matched ? undefined : typedOrg || undefined,
        requestId,
        inviteName.trim() || undefined,
      );
      setInviteEmail("");
      setInviteName("");
      setInviteOrgName("");
      setInviting(false);
      onForwarded();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to send the invitation",
      );
    } finally {
      setInviteSubmitting(false);
    }
  };

  const userRow = (u: RedirectableUser, indent: boolean) => {
    const locked = prevUserIDs.has(u.id);
    const checked = selectedUserIDs.has(u.id) || locked;
    return (
      <label
        key={`user-${u.id}`}
        className={`fwd-user-row ${indent ? "indent" : ""} ${
          locked ? "locked" : ""
        } ${checked && !locked ? "checked" : ""}`}
      >
        <input
          type="checkbox"
          checked={checked}
          disabled={locked}
          onChange={() => !locked && toggleUser(u.id)}
        />
        <span className="fwd-user-text">
          <span className="fwd-user-name">{u.name}</span>
          <span className="fwd-user-email"> {u.email}</span>
          <span className={`fwd-role-tag ${u.role === "pi" ? "pi" : ""}`}>
            {u.role === "pi" ? "PI" : "Uploader"}
          </span>
          {locked && <span className="fwd-sent-tag">Already forwarded</span>}
        </span>
      </label>
    );
  };

  const orgs = targets?.organizations || [];
  const hasAnyTarget = allUsers.length > 0;

  return (
    <div className="fwd-panel">
      <style>{`
        .fwd-panel { margin-top: 4px; }
        .fwd-intro {
          font-size: 13px;
          line-height: 1.5;
          color: var(--text-secondary);
          margin: 0 0 10px;
        }
        .fwd-tree {
          border: 0.5px solid var(--border-strong);
          border-radius: var(--radius);
          background: var(--surface-2);
          max-height: 300px;
          overflow-y: auto;
          margin-bottom: 12px;
        }
        .fwd-org-row, .fwd-user-row {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 13px;
          border-bottom: 0.5px solid var(--border);
          cursor: pointer;
        }
        .fwd-tree > *:last-child { border-bottom: none; }
        .fwd-org-row {
          padding: 8px 12px;
          background: var(--surface-1);
          font-weight: 500;
        }
        .fwd-org-row.locked { color: var(--text-muted); cursor: default; }
        .fwd-org-expand {
          background: none;
          border: none;
          padding: 2px;
          display: flex;
          align-items: center;
          cursor: pointer;
          color: var(--text-muted);
        }
        .fwd-user-row { padding: 7px 12px; }
        .fwd-user-row.indent { padding-left: 42px; }
        .fwd-user-row:hover:not(.locked) { background: var(--surface-1); }
        .fwd-user-row.checked { background: var(--bg-accent-muted); }
        .fwd-user-row.locked {
          background: var(--surface-1);
          color: var(--text-muted);
          cursor: default;
        }
        .fwd-user-row input, .fwd-org-row input {
          width: 15px; height: 15px; flex-shrink: 0;
          accent-color: var(--border-accent);
        }
        .fwd-user-text { flex: 1; min-width: 0; }
        .fwd-user-name { font-weight: 500; }
        .fwd-user-email { color: var(--text-muted); }
        .fwd-role-tag {
          font-size: 10px;
          padding: 1px 6px;
          border-radius: 3px;
          font-weight: 600;
          margin-left: 6px;
          background: var(--bg-warning);
          color: var(--text-warning);
          white-space: nowrap;
        }
        .fwd-role-tag.pi {
          background: var(--bg-accent-muted);
          color: var(--text-accent);
        }
        .fwd-sent-tag {
          font-size: 10px;
          padding: 1px 6px;
          border-radius: 3px;
          margin-left: 6px;
          background: var(--surface-0);
          border: 0.5px solid var(--border-strong);
          color: var(--text-muted);
          white-space: nowrap;
        }
        .fwd-invite {
          border-top: 0.5px solid var(--border);
          margin-top: 10px;
          padding-top: 12px;
        }
        .fwd-invite-input {
          width: 100%;
          box-sizing: border-box;
          padding: 7px 10px;
          margin-bottom: 6px;
          border: 0.5px solid var(--border-strong);
          border-radius: var(--radius);
          font-size: 13px;
          font-family: inherit;
          color: var(--text-primary);
          background: var(--surface-2);
        }
        .fwd-invite-hint {
          margin: 2px 0 10px;
          font-size: 12px;
          color: var(--text-muted);
        }
        .fwd-group-label {
          font-size: 11px;
          text-transform: uppercase;
          letter-spacing: 0.04em;
          color: var(--text-muted);
          padding: 8px 12px 4px;
          border-bottom: 0.5px solid var(--border);
        }
        .fwd-actions { display: flex; gap: 8px; }
        .fwd-btn {
          padding: 8px 14px;
          border-radius: var(--radius);
          font-size: 13px;
          font-family: inherit;
          font-weight: 500;
          cursor: pointer;
          border: 0.5px solid var(--border-strong);
          background: var(--surface-2);
          color: var(--text-primary);
        }
        .fwd-btn:hover:not(:disabled) { background: var(--surface-1); }
        .fwd-btn.primary {
          background: var(--fill-primary);
          color: var(--on-primary);
          border-color: transparent;
        }
        .fwd-btn:disabled {
          background: var(--fill-disabled);
          color: var(--text-disabled);
          border-color: var(--border-disabled);
          cursor: not-allowed;
        }
        .fwd-error {
          font-size: 12px;
          color: var(--text-danger);
          background: var(--bg-danger);
          border: 0.5px solid var(--border-danger);
          border-radius: var(--radius);
          padding: 8px 10px;
          margin-bottom: 10px;
        }
        .fwd-empty {
          font-size: 13px;
          color: var(--text-muted);
          padding: 14px 12px;
        }
      `}</style>

      {error && <p className="fwd-error">{error}</p>}

      {loading ? (
        <p className="fwd-empty">Loading recipients…</p>
      ) : !hasAnyTarget ? (
        <p className="fwd-empty">
          No data uploaders or PIs are available to forward this request to.
        </p>
      ) : (
        <>
          <p className="fwd-intro">
            Select one or more recipients. Check an organisation to select all of
            its members, or expand it to pick specific people.
          </p>

          <div className="fwd-tree">
            {orgs.map((org) => {
              const members = orgMembers.get(org.id) || [];
              if (members.length === 0) return null;
              const selectable = members.filter((u) => !prevUserIDs.has(u.id));
              const orgLocked =
                prevOrgIDs.has(org.id) || selectable.length === 0;
              const allSelected =
                selectable.length > 0 &&
                selectable.every((u) => selectedUserIDs.has(u.id));
              const someSelected =
                !allSelected && selectable.some((u) => selectedUserIDs.has(u.id));
              const open = expandedOrgs.has(org.id);
              return (
                <React.Fragment key={`org-${org.id}`}>
                  <div className={`fwd-org-row ${orgLocked ? "locked" : ""}`}>
                    <button
                      type="button"
                      className="fwd-org-expand"
                      onClick={() => toggleExpand(org.id)}
                      aria-label={open ? "Collapse" : "Expand"}
                      aria-expanded={open}
                    >
                      <ChevronIcon open={open} />
                    </button>
                    <input
                      type="checkbox"
                      checked={allSelected || prevOrgIDs.has(org.id)}
                      disabled={orgLocked}
                      ref={(el) => {
                        if (el) el.indeterminate = someSelected;
                      }}
                      onChange={() => !orgLocked && toggleOrg(members)}
                    />
                    <span style={{ flex: 1 }}>
                      {org.name}
                      {prevOrgIDs.has(org.id) && (
                        <span className="fwd-sent-tag">Already forwarded</span>
                      )}
                    </span>
                  </div>
                  {open && members.map((u) => userRow(u, true))}
                </React.Fragment>
              );
            })}

            {unaffiliated.length > 0 && (
              <>
                <p className="fwd-group-label">No organisation</p>
                {unaffiliated.map((u) => userRow(u, false))}
              </>
            )}
          </div>

          <textarea
            className="arv-notes"
            rows={2}
            placeholder="Note for the recipient (optional)…"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            disabled={submitting}
          />

          {inviting ? (
            <div className="fwd-invite">
              <p className="fwd-group-label">Invite a new data supplier</p>
              <input
                className="fwd-invite-input"
                type="email"
                placeholder="Email address (required)"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                disabled={inviteSubmitting}
              />
              <input
                className="fwd-invite-input"
                type="text"
                placeholder="Name (optional)"
                value={inviteName}
                onChange={(e) => setInviteName(e.target.value)}
                disabled={inviteSubmitting}
              />
              <input
                className="fwd-invite-input"
                type="text"
                placeholder="Organisation (optional)"
                value={inviteOrgName}
                onChange={(e) => setInviteOrgName(e.target.value)}
                list={`fwd-orgs-${requestId}`}
                disabled={inviteSubmitting}
              />
              <datalist id={`fwd-orgs-${requestId}`}>
                {(targets?.organizations || []).map((o) => (
                  <option key={o.id} value={o.name} />
                ))}
              </datalist>
              <p className="fwd-invite-hint">
                They get an upload link for this request. The note above is
                included in the invitation email.
              </p>
              <div className="fwd-actions">
                <button
                  type="button"
                  className="fwd-btn primary"
                  onClick={handleInvite}
                  disabled={inviteSubmitting || !inviteEmail.trim()}
                >
                  {inviteSubmitting ? "Sending…" : "Send invitation"}
                </button>
                <button
                  type="button"
                  className="fwd-btn"
                  onClick={() => setInviting(false)}
                  disabled={inviteSubmitting}
                >
                  Back
                </button>
              </div>
            </div>
          ) : (
          <div className="fwd-actions">
            <button
              type="button"
              className="fwd-btn primary"
              onClick={handleSubmit}
              disabled={submitting || selectedUserIDs.size === 0}
              title={
                selectedUserIDs.size === 0
                  ? "Select at least one recipient"
                  : undefined
              }
            >
              {submitting
                ? "Forwarding…"
                : selectedUserIDs.size === 0
                  ? "Forward"
                  : `Forward to ${selectedUserIDs.size} recipient${
                      selectedUserIDs.size === 1 ? "" : "s"
                    }`}
            </button>
            <button
              type="button"
              className="fwd-btn"
              onClick={() => setInviting(true)}
              disabled={submitting}
            >
              Invite someone not listed…
            </button>
            <button
              type="button"
              className="fwd-btn"
              onClick={onCancel}
              disabled={submitting}
            >
              Cancel
            </button>
          </div>
          )}
        </>
      )}
    </div>
  );
};
